import { TranslateFunction } from 'Types';
import { RouteComponentProps } from 'react-router-dom';
import ReactGA from 'react-ga4';

ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS || '');

export default class CurrentRouter {
  static match: any = null;
  private static onLeaveCallback?: (() => void) | null;
  private static history: any = null;
  private static unlisten: any;
  private static stack: any[] = [];
  private static hashParams: URLSearchParams = new URLSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  private static t: TranslateFunction = (s, r) => s;
  private static confirmDialog: any = null;

  private static allowNextTry: boolean = false;

  static load(routerProps: RouteComponentProps) {
    if (CurrentRouter.history !== routerProps.history) {
      if (CurrentRouter.unlisten) CurrentRouter.unlisten();
      CurrentRouter.unlisten = routerProps.history.listen(async (h) => {
        if (!CurrentRouter.allowNextTry && CurrentRouter.onLeaveCallback) {
          // @ts-ignore
          const willAsk = CurrentRouter.onLeaveCallback && !CurrentRouter.displayingConfirmDialog;
          CurrentRouter.allowNextTry = true;
          CurrentRouter.history.go(1);
          if (CurrentRouter.displayingConfirmDialog) return;
          if (!(await CurrentRouter.discardChanges())) return;
          if (willAsk) {
            CurrentRouter.allowNextTry = true;
            let i: number = -1;
            const len: number = CurrentRouter.stack.length || 0;
            for (i = 0; i < len; i++) {
              if (CurrentRouter.stack[len - i - 1].pathname !== h.pathname) break;
            }
            ReactGA.set({ page: h.pathname }); // Update the user's current page
            ReactGA.send({ hitType: 'page_view', page: h.pathname }); // Record a pageview for the given page
            CurrentRouter.history.go(-i);
            return;
          }
        }
        ReactGA.set({ page: h.pathname }); // Update the user's current page
        ReactGA.send({ hitType: 'page_view', page: h.pathname }); // Record a pageview for the given page

        CurrentRouter.allowNextTry = false;
        let i: number = -1;
        for (i = CurrentRouter.stack.length - 1; i >= 0; i--) {
          if (CurrentRouter.stack[i].key === h.key) break;
        }
        if (i === -1) {
          CurrentRouter.stack.push(h);
        } else {
          CurrentRouter.stack.length = i + 1;
        }
      });
      CurrentRouter.stack.push(routerProps.history.location);
      CurrentRouter.history = routerProps.history;
      // @ts-ignore
      window.CurrentRouter = CurrentRouter;
    }
    CurrentRouter.match = routerProps.match;
    CurrentRouter.hashParams = new URLSearchParams(routerProps.location.hash.replace('#', ''));
  }

  static push(path: string, state?: any): boolean {
    if (!CurrentRouter.history) return false;
    if (CurrentRouter.onLeaveCallback) {
      CurrentRouter.discardChanges().then((res) => {
        if (!res) return;
        CurrentRouter.history.push(path, state);
      });
      return false;
    }
    CurrentRouter.history.push(path, state);
    return true;
  }

  private static displayingConfirmDialog: boolean = false;
  static discardChanges(): Promise<boolean> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars,consistent-return
    return new Promise((resolve, reject) => {
      if (CurrentRouter.displayingConfirmDialog) return resolve(false);
      if (!CurrentRouter.history) return resolve(false);
      if (!CurrentRouter.onLeaveCallback) return resolve(true);
      CurrentRouter.displayingConfirmDialog = true;
      CurrentRouter.confirmDialog(
        CurrentRouter.t('Do you want to discard those changes?').split('\n'),
        CurrentRouter.t('You have unsaved changes...'),
        CurrentRouter.t('Stay here'),
        CurrentRouter.t('Discard changes'),
        () => {
          CurrentRouter.displayingConfirmDialog = false;
          resolve(false);
        },
        () => {
          CurrentRouter.displayingConfirmDialog = false;
          const { onLeaveCallback } = CurrentRouter;
          CurrentRouter.onLeaveCallback = undefined;
          if (onLeaveCallback) onLeaveCallback();
          resolve(true);
        },
        { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
      );
    });
  }

  static listen(listener: any) {
    if (!CurrentRouter.history) return null;
    return CurrentRouter.history.listen(listener);
  }

  static onLeave(callback: (() => void) | null) {
    if (callback) {
      const last = CurrentRouter.stack[CurrentRouter.stack.length - 2] || {};
      if (last.pathname !== window.location.pathname) CurrentRouter.history.push('#');
    }
    CurrentRouter.onLeaveCallback = callback;
  }

  static setHashValue(key: string, value: string) {
    if ((CurrentRouter.hashParams.get(key) || '') === value) return;
    if (!value) {
      CurrentRouter.hashParams.delete(key);
    } else {
      CurrentRouter.hashParams.set(key, value);
    }
    CurrentRouter.push(`#${CurrentRouter.hashParams.toString()}`);
  }

  static getHashValue(key: string) {
    return CurrentRouter.hashParams.get(key);
  }

  static getHashString() {
    const hash = CurrentRouter.hashParams.toString();
    if (!hash) return '';
    return `#${hash}`;
  }

  static onbeforeunload() {
    if (CurrentRouter.onLeaveCallback) return true;
    return null;
  }

  static setTranslationFunction(t: TranslateFunction) {
    CurrentRouter.t = t;
  }

  static setConfirmDialog(confirmDialog: any) {
    CurrentRouter.confirmDialog = confirmDialog;
  }
}
